// @ts-nocheck
import { v4 as uuid } from 'uuid';
import * as FlatModels from '@medsurf/flat-models';
import { Element } from '../element';
import { Selection } from './selection';
import { Format } from '../../../../../common/models/format';
import { Label } from './label/label';
import { Line } from './pointer/line';
import { Pointer } from './pointer/pointer';
import { Keymap } from './keymap';
import { KeymapSelftest } from './keymapSelftest';

/**
 * Marker
 */
export class Marker extends Element<FlatModels.ImageSharedModels.MarkerModel> {
  /**
   * Members
   */
  private _labels: any[];
  private _toolTips: any[];
  private _pointers: any[];
  private _labelOffset: number;
  private _labelOffsetY: number;
  private _selection: any;
  private _forkLine: any;

  /**
   * Constructor
   *
   * @param model
   * @param _format
   * @param localPaper
   * @param _mainLayer
   * @param container
   * @param _imageScale
   * @param _imageOffset
   */
  public constructor(model: FlatModels.ImageSharedModels.MarkerModel,
                     private _format: Format,
                     public localPaper,
                     private _mainLayer,
                     public container,
                     public _imageScale = 1,
                     public _imageOffset?) {
    super(model);
    this._labels = [];
    this._toolTips = [];
    this._pointers = [];
    this._labelOffset = 5;
    this._labelOffsetY = 20;
    this._forkLine = null;
    if (this.model.annotation && this.model.source) {
      this._init();
    }
  }

  /**
   * Init
   *
   * @protected
   */
  protected _init() {
    this._element = new this.localPaper.Group();
    this._mainLayer.addChild(this._element);
    this.draw();
  }

  /**
   * Draw
   */
  public draw() {
    this.cleanUp();
    if (!this.model.source) {
      return;
    }
    if (this.hasPointer()) {
      this.drawPointers();
      this.drawForkLine();
    }
    this.drawLabels();
    this.drawSelection();
    this.arrange();
  }

  /**
   * Draw Labels
   */
  public drawLabels() {
    if (!this.model.label) { return; }
    if (!this.model.label.text) { return; }
    const label = new Label({id: uuid(), text: this.model.label.text}, this._format, this);
    this._element.addChild(label.element);
    this._labels.push(label);
  }

  /**
   * Draw Pointers
   */
  public drawPointers() {
    for (const target of this.model.targets) {
      let source = this.container.getAbsoluteCoords(
        this.model.source.position.x,
        this.model.source.position.y,
        this._imageOffset,
        this._imageScale
      );
      if (this.model.annotation.fork.x && this.model.annotation.fork.y) {
        source = this.container.getAbsoluteCoords(
          this.model.annotation.fork.x,
          this.model.annotation.fork.y,
          this._imageOffset,
          this._imageScale
        );
      }
      const pointer = new Pointer(
        (target.endType) ? (target.endType) : 'dot',
        this._format,
        source,
        this.container.getAbsoluteCoords(target.position.x, target.position.y, this._imageOffset, this._imageScale),
        this
      );
      this._element.addChild(pointer.end.element.element);
      this._element.addChild(pointer.line.element);
      this._pointers.push(pointer);
    }
  }

  /**
   * Draw Selection
   */
  public drawSelection() {
    if (this._selection) {
      this.removeSelection();
    }
    this._selection = new Selection(this._model, this._format, this.localPaper, this.container, this);
    this._element.addChild(this._selection.element);
  }

  /**
   * Remove Selection
   */
  public removeSelection() {
    if (this._selection) {
      this._selection.cleanUp();
      this._selection = null;
    }
  }

  /**
   * Draw Fork Line
   */
  public drawForkLine() {
    if (this.model.annotation.fork.x && this.model.annotation.fork.y) {
      if (this._forkLine) {
        this.removeForkLine();
      }
      const source = this.container.getAbsoluteCoords(
        this.model.source.position.x,
        this.model.source.position.y,
        this._imageOffset,
        this._imageScale
      );
      const target = this.container.getAbsoluteCoords(
        this.model.annotation.fork.x,
        this.model.annotation.fork.y,
        this._imageOffset,
        this._imageScale
      );
      this._forkLine = new Line(this._format, source, target, this);
      this._forkLine.element.marker = this;
      this._element.addChild(this._forkLine.element);
    }
  }

  /**
   * Arrange
   */
  public arrange() {
    if (!this.model || !this.model.source) {
      return;
    }
    const zoom = this.localPaper.view.zoom;
    const coords = this.container.getAbsoluteCoords(
      this.model.source.position.x,
      this.model.source.position.y,
      this._imageOffset,
      this._imageScale
    );
    let forkCoords = coords;
    this.scale(zoom);
    if (this._labels.length > 0) {
      this._labels[0].setPosition(coords.x, coords.y);
    }
    if (this._forkLine) {
      this._forkLine.setScale(zoom);
    }
    if (this._isSelected) {
      if (this._selection && this._selection.rectangle) {
        if (this._model.annotation.fork.x && this._model.annotation.fork.y) {
          forkCoords = this.container.getAbsoluteCoords(
              this._model.annotation.fork.x,
              this._model.annotation.fork.y,
              this._imageOffset,
              this._imageScale
          );
        }
        this._selection.setScale(zoom);
        this._selection.rectangle.position = coords;
        if (this._selection.circle) {
          this._selection.circle.position = forkCoords;
        }
        this._selection.arrange();
        this._selection.element.bringToFront();
      }
    } else {
      this.removeSelection();
    }
    this.state = this._state;
  }

  /**
   * Scale
   *
   * @param zoom
   */
  public scale(zoom: number) {
    if (this._labels.length > 0) {
      this._labels[0].setScale(zoom);
    }
    for (const pointer of this._pointers) {
      pointer.setScale(zoom);
    }
  }

  /**
   * Is In View Port
   *
   * @param rect
   */
  public isInViewPort(rect) {
    const viewRect = this.localPaper.view.bounds;
    return (rect.x + rect.width) < (viewRect.x + viewRect.width)
      && (rect.x) > (viewRect.x)
      && (rect.y) > (viewRect.y)
      && (rect.y + rect.height) < (viewRect.y + viewRect.height);
  }

  /**
   * Set Position
   *
   * @param x
   * @param y
   */
  public setPosition(x: number,
                     y: number) {
    this._element.position.x = x;
    this._element.position.y = y;
  }

  /**
   * Resize
   *
   * @param w
   * @param h
   */
  public resize(w: number,
                h: number) {
    this.cleanUp();
    this.draw();
  }

  /**
   * @example
   *   // state: visible/default
   *   this.setVisibilitySettings(
   *     {labels: true,
   *     pointers: true,
   *     selftests: false,
   *     tooltips: false
   *     }
   *
   * @param isVisible Object
   * @this Marker
   * @return void
   */
  public setVisibilitySettings(isVisible) {
    for (const label of this._labels) {
      label._element.visible = isVisible.labels;
    }
    for (const pointer of this._pointers) {
      // eslint-disable-next-line no-prototype-builtins
      if (isVisible.hasOwnProperty('index')) {
        pointer._line._element.visible = (this._pointers.indexOf(pointer) === isVisible.index);
        pointer._end._element._element.visible = pointer._end._endType === 'none' ?
          false :
          (this._pointers.indexOf(pointer) === isVisible.index);
      } else {
        pointer._line._element.visible = isVisible.pointers;
        pointer._end._element._element.visible = pointer._end._endType === 'none' ? false : isVisible.pointers;
      }
    }
    if (this._forkLine) {
      this._forkLine.element.visible = isVisible.pointers;
    }
    if (isVisible.selftests === false) {
      // remove old label
      if (this.container._selftestLabel) {
        this.container.removeElement(this.container._selftestLabel);
      }

      // draw new label if index is set
      if (isVisible.index !== undefined) {
        // Get keymap element
        const keymaps = this.container._elements.filter((element) => {
          return element instanceof Keymap;
        });

        if (keymaps.length > 0) {
          const key = keymaps
            .map((keymap) => keymap.model.columns).pop()
            .map((column) => column.labels)
            .reduce((a, b) => a.concat(b))
            .filter((value) => String(value.index) === String(this.model.label.text)).pop();

          if (key) {
            // setup keymap selftest
            this.container._selftestLabel = new KeymapSelftest(
              keymaps[0],
              this._format,
              this.localPaper,
              this._mainLayer,
              this.container,
              key,
              this._imageScale,
              this._imageOffset
            );
            this.container._selftestLabel.id = 'marker_selftest';
            this.container.addElement(this.container._selftestLabel);
            this.container._selftestLabel._element.bringToFront();
          }
        }
      }
    }
  }

  /**
   * Show Selected Marker
   *
   * @param index
   */
  public showSelectedMarker(index: number) {
    this._state = 'selected';
    this.setVisibilitySettings({labels: true, pointers: true, selftests: false, index: index});
  }

  /**
   * Getter useSymbol
   */
  public get useSymbol() {
    if (this._labels.length > 0) {
      return this._labels[0].useSymbol;
    }
    return false;
  }

  /**
   * Getter pointers
   */
  public get pointers() {
    return (this._pointers) ? this._pointers : null;
  }

  /**
   * Getter labels
   */
  public get labels() {
    return (this._labels) ? this._labels : null;
  }

  /**
   * Setter state
   *
   * There are four different states for each Marker object:
   *
   *  - default/visible: Toggle Marker is active, Toggle Selftest is not active
   *                     Pointers and Labels are visible, Selftests and Tooltips are hidden
   *  - hidden:          Toggle Marker is not active
   *                     All subelements/children are hidden
   *  - unselected:      Toggle Marker and Toggle Selftest are active
   *                     One of two states in selftest mode. Current marker has not been selected yet.
   *                     Only Selftest circles are visible
   *  - selected:        Toggle Marker and Toggle Selftest are active
   *                     One of two states in selftest mode. Current marker is selected.
   *                     Selftest circles and Tooltips are visible
   *
   * @param {string} state - State of this Marker object
   * @this Marker
   * @returns void
   */
  public set state(state) {
    let isVisible = {labels: true, pointers: true, selftests: false, tooltips: false};
    this._state = state;
    if (!this._element) {
      return;
    }
    switch (state) {
      case 'hidden':
        isVisible = {labels: false, pointers: false, selftests: false, tooltips: false};
        this._element.opacity = 1;
        break;
      case 'unselected':
        isVisible = {labels: false, pointers: false, selftests: true, tooltips: false};
        this._element.opacity = 1;
        break;
      case 'selected':
        isVisible = {labels: false, pointers: false, selftests: true, tooltips: true};
        this._element.opacity = 1;
        break;
      case 'opaque':
        isVisible = {labels: false, pointers: false, selftests: true, tooltips: false};
        this._element.opacity = 0; // should be 0.3, bug paperjs has issues with opacity
        break;
      default:
        this._state = 'visible';
        this._element.opacity = 1;
        break;
    }
    this.setVisibilitySettings(isVisible);
  }

  /**
   * Remove Label
   */
  public removeLabels() {
    for (let label of this._labels) {
      label.cleanUp();
      label = null;
    }
    this._labels = [];
  }

  /**
   * Remove Tool Tips
   */
  removeToolTips() {
    for (let toolTip of this._toolTips) {
      toolTip.cleanUp();
      toolTip = null;
    }
    this._toolTips = [];
  }

  /**
   * Remove Pointers
   */
  public removePointers() {
    for (let pointer of this._pointers) {
      pointer.cleanUp();
      pointer = null;
    }
    this._pointers = [];
  }

  /**
   * Remove Fork Line
   */
  public removeForkLine() {
    if (this._forkLine) {
      this._forkLine.cleanUp();
      this._forkLine = null;
    }
  }

  /**
   * Has Pointer
   */
  public hasPointer() {
    let hasPointer = false;
    if (this._model.targets && this._model.targets.length > 0) {
      hasPointer = true;
      if (this._model.targets.length === 1) {
        if (this._model.source && this._model.targets[0] && this._model.targets[0].position.x === this._model.source.position.x &&
          this._model.targets[0].position.y === this._model.source.position.y) {
          hasPointer = false;
        }
      }
    }
    return hasPointer;
  }

  /**
   * Cleanup
   */
  public cleanUp() {
    this.removeSelection();
    this.removeLabels();
    this.removeToolTips();
    this.removePointers();
    this.removeForkLine();
  }
}
